<template>
  <b-card>
    <b-alert
      variant="info"
      show
      class="mb-2"
    >
      <div class="alert-body">
        Токен доступа заменяет ваш логин и пароль при обращении к API и при подключении
        ИИ-инструментов. Права у него <b>ровно ваши</b> — ни больше, ни меньше, — а все действия
        под ним попадают в журнал с вашим именем.
      </div>
    </b-alert>

    <!-- секрет показывается ОДИН раз, сразу после выпуска -->
    <b-alert
      v-if="issuedToken"
      variant="warning"
      show
      class="mb-2"
    >
      <h4 class="alert-heading">
        Скопируйте токен сейчас
      </h4>
      <div class="alert-body">
        <p>
          Он показывается <b>один раз</b> и восстановить его нельзя — платформа хранит только
          отпечаток. Если потеряете, выпустите новый.
        </p>
        <b-input-group class="input-group-merge">
          <b-form-input
            :value="issuedToken"
            readonly
            class="font-small-3"
          />
          <b-input-group-append>
            <b-button
              variant="outline-primary"
              @click="copyToken"
            >
              Скопировать
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-alert>

    <div
      v-if="loading"
      class="text-center my-2"
    >
      <b-spinner small /> Загрузка…
    </div>

    <!-- текущее состояние -->
    <template v-else>
      <div v-if="hasToken">
        <b-row>
          <b-col md="6">
            <b-form-group label="Идентификатор">
              <b-form-input
                :value="info.tokenid"
                readonly
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Действует до">
              <b-form-input
                :value="expiresText"
                readonly
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Последнее использование">
              <b-form-input
                :value="info.lastused || 'ещё не использовался'"
                readonly
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Разрешённые адреса">
              <b-form-input
                :value="info.iplist || 'без ограничения'"
                readonly
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-alert
          v-if="info.expired"
          variant="danger"
          show
        >
          <div class="alert-body">
            Срок токена истёк — выпустите новый, старый уже не работает.
          </div>
        </b-alert>
        <b-alert
          v-else-if="info.eternal"
          variant="warning"
          show
        >
          <div class="alert-body">
            Токен <b>бессрочный</b>. Он не перестанет работать сам — если он утечёт, единственная
            защита это отзыв вручную.
          </div>
        </b-alert>
        <b-alert
          v-else-if="soonExpires"
          variant="warning"
          show
        >
          <div class="alert-body">
            Осталось дней: {{ info.days_left }}. После истечения ИИ-клиент и скрипты перестанут
            работать — перевыпустите заранее.
          </div>
        </b-alert>
      </div>

      <b-alert
        v-else
        variant="secondary"
        show
      >
        <div class="alert-body">
          Токен не выпущен.
        </div>
      </b-alert>

      <hr class="my-2">

      <!-- выпуск -->
      <b-row>
        <b-col md="4">
          <b-form-group label="Срок действия">
            <b-form-select
              v-model="ttl"
              :options="ttlOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="ttl === 'custom'"
          md="4"
        >
          <b-form-group label="Дней">
            <b-form-input
              v-model.number="customDays"
              type="number"
              min="1"
              max="3650"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Разрешённые адреса (необязательно)">
            <b-form-input
              v-model="iplist"
              placeholder="192.168.1.0/24, 10.0.0.5"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert
        v-if="hasToken"
        variant="warning"
        show
      >
        <div class="alert-body">
          У пользователя может быть только один токен. Выпуск нового <b>немедленно прекращает</b>
          работу прежнего — везде, где он вставлен.
        </div>
      </b-alert>

      <b-button
        variant="primary"
        :disabled="busy"
        class="mr-1"
        @click="issue"
      >
        <b-spinner
          v-if="busy"
          small
          class="mr-50"
        />
        {{ hasToken ? 'Выпустить заново' : 'Выпустить токен' }}
      </b-button>
      <b-button
        v-if="hasToken"
        variant="outline-danger"
        :disabled="busy"
        @click="revoke"
      >
        Отозвать
      </b-button>
    </template>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BAlert, BSpinner,
  BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/cubus/jwt/useJwt'

/**
 * PROP-230: раздел «Токен доступа» в настройках учётной записи.
 *
 * Секрет живёт ТОЛЬКО в этом компоненте и только до перезагрузки страницы: сервер отдаёт его один
 * раз при выпуске и больше не отдаёт никогда. Ни в localStorage, ни в Vuex он не кладётся — иначе
 * долгоживущий доступ осел бы в браузере, ровно там, откуда его и уводят (урок PROP-086).
 */
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BAlert,
    BSpinner,
    BInputGroup,
    BInputGroupAppend,
  },
  data() {
    return {
      loading: true,
      busy: false,
      info: {},
      issuedToken: '',
      ttl: 30,
      customDays: 90,
      iplist: '',
      ttlOptions: [
        { value: 30, text: '30 дней (по умолчанию)' },
        { value: 90, text: '90 дней' },
        { value: 365, text: 'год' },
        { value: 'custom', text: 'свой срок' },
        { value: 0, text: 'бессрочно' },
      ],
    }
  },
  computed: {
    hasToken() {
      return !!(this.info && this.info.tokenid)
    },
    expiresText() {
      if (!this.hasToken) return ''
      return this.info.eternal ? 'бессрочно' : this.info.expires
    },
    soonExpires() {
      const d = parseInt(this.info.days_left, 10)
      return !Number.isNaN(d) && d <= 7
    },
  },
  created() {
    this.load()
  },
  methods: {
    call(payload) {
      return useJwt.apiToken(payload).then(res => res.data)
    },
    toast(title, variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon', variant, text,
        },
      })
    },
    load() {
      this.loading = true
      this.call({ action: 'info' })
        .then(data => {
          this.info = (data && data.token) || {}
        })
        .catch(e => this.toast('Не удалось прочитать токен', 'danger', this.errText(e)))
        .finally(() => { this.loading = false })
    },
    issue() {
      const days = this.ttl === 'custom' ? this.customDays : this.ttl
      this.busy = true
      this.call({ action: 'issue', days, iplist: this.iplist })
        .then(data => {
          if (data && data.token) {
            this.issuedToken = data.token
            this.info = data.info || {}
            this.toast('Токен выпущен', 'success', 'Скопируйте его — он показывается один раз.')
          } else {
            this.toast('Токен не выпущен', 'danger', (data && data.error) || '')
          }
        })
        .catch(e => this.toast('Токен не выпущен', 'danger', this.errText(e)))
        .finally(() => { this.busy = false })
    },
    revoke() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Отозвать токен? Всё, что им пользуется, перестанет работать сразу.')) return
      this.busy = true
      this.call({ action: 'revoke' })
        .then(() => {
          this.issuedToken = ''
          this.info = {}
          this.toast('Токен отозван', 'success', '')
          this.load()
        })
        .catch(e => this.toast('Не удалось отозвать', 'danger', this.errText(e)))
        .finally(() => { this.busy = false })
    },
    copyToken() {
      const done = () => this.toast('Скопировано', 'success', '')
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.issuedToken).then(done).catch(() => this.fallbackCopy())
      } else {
        this.fallbackCopy()
      }
    },
    fallbackCopy() {
      // Старые браузеры и http-контур: `navigator.clipboard` там недоступен.
      const el = document.createElement('textarea')
      el.value = this.issuedToken
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      this.toast('Скопировано', 'success', '')
    },
    errText(e) {
      return (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || ''
    },
  },
}
</script>
